import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { NewInput } from '../../../Components/PIM/common';
import { AddIcon, RearrangeIcon, DeleteIcon } from '../../../bombiix-icon';

const style = {
  cursor: 'move',
};

/**
 * Single manual-column option row (drag-reorder + add / remove). Shared by
 * ProductCP, OprationalCP and PIM.
 *
 * When the opt-in `enforceOptionRules` flag is true (PIM), the add button trims
 * the value, blocks empty / whitespace-only entries and case-sensitive exact
 * duplicates. The flag defaults off so the legacy display-only behaviour
 * (plain `<input>` + `error-message` text) for the other modules is unchanged.
 *
 * PIM's invalid state uses master-design `NewInput` (red border + trailing
 * alert icon, BM-9522) instead of inline error text: the old
 * `position-absolute` error span didn't reserve layout space, so it rendered
 * behind the next option row's input instead of pushing it down.
 *
 * `enforceOptionRules` also swaps the add/rearrange/delete glyphs from the
 * legacy icon font to the `bombiix-icon` set for PIM; ProductCP/OprationalCP
 * keep the legacy icon-font glyphs unchanged.
 */
function RenderOptionsItem({ column, index, moveCard, parentIndex, id, remove, addNew, handleChange, cardEle, wholeArry, handleDefaultChange, enforceOptionRules = false }) {
  const ref = useRef(null);
  const { t } = useTranslation('common');
  const [addError, setAddError] = useState(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(ele, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = ele.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, cardEle, parentIndex);
      ele.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  const showDefaultCheckbox = ['Dropdown', 'Multi_select_Dropdown'].includes(cardEle?.manualType?.key);
  const normalizedName = (column?.name ?? '').trim().toLowerCase();
  const isDuplicateOption =
    normalizedName !== '' &&
    (wholeArry ?? []).some((opt, i) => i !== index && (opt?.name ?? '').trim().toLowerCase() === normalizedName);

  const trimmedName = (column?.name ?? '').trim();
  const isCaseSensitiveDuplicate =
    trimmedName !== '' &&
    (wholeArry ?? []).some((opt, i) => i !== index && (opt?.name ?? '').trim() === trimmedName);

  const handleAddClick = () => {
    if (!enforceOptionRules) {
      addNew(parentIndex);
      return;
    }
    if (trimmedName === '') {
      setAddError('empty');
      return;
    }
    if (isCaseSensitiveDuplicate) {
      setAddError('duplicate');
      return;
    }
    setAddError(null);
    addNew(parentIndex);
  };

  const handleNameChange = (event) => {
    if (enforceOptionRules) setAddError(null);
    handleChange(event.target.value, index, parentIndex);
  };

  const addRuleInvalid = enforceOptionRules && Boolean(addError);
  const isInvalid = addRuleInvalid || isDuplicateOption || Boolean(cardEle?.hasError && !column?.name);

  return (
    <div className="d-flex" key={`${column?.Nmae}_${index}`} style={{ ...style, opacity }} data-handler-id={handlerId} ref={ref}>
      <div className="common-input-control  w-100">
        {enforceOptionRules ? (
          <NewInput
            type="text"
            onChange={handleNameChange}
            placeholder={t('PRODUCT_CRITICAL_PATH.MODAL.RENDER_OPTION_SITE_ITEM.ENTER_OPTIONS')}
            value={column?.name}
            maxLength={30}
            invalid={isInvalid}
          />
        ) : (
          <>
            <input
              type="text"
              onChange={handleNameChange}
              placeholder={t('PRODUCT_CRITICAL_PATH.MODAL.RENDER_OPTION_SITE_ITEM.ENTER_OPTIONS')}
              value={column?.name}
              maxLength="30"
              className="form-control text-bordered"
            />
            <span className="error-message position-absolute top-32">
              {isDuplicateOption
                ? t('PRODUCT_CRITICAL_PATH.MODAL.RENDER_OPTION_SITE_ITEM.OPTION_ALREADY_EXISTS')
                : cardEle?.hasError && !column?.name && t('PRODUCT_CRITICAL_PATH.MODAL.RENDER_OPTION_SITE_ITEM.PLEASE_ENTER_OPTION_VALUE')}
            </span>
          </>
        )}
      </div>
      {wholeArry.length - 1 === index && (
        <button type="button" aria-label="add" className="btn ml-2 btn-outline sm" onClick={handleAddClick}>
          {enforceOptionRules ? <AddIcon size={20} /> : <i className="icon-Add f-20" />}
        </button>
      )}
      <button type="button" aria-label="expand" className="btn ml-2 btn-outline sm" id={column?.name}>
        {enforceOptionRules ? <RearrangeIcon size={20} /> : <i className="icon-Rearrange" />}
      </button>
      {wholeArry.length > 1 && (
        <button type="button" onClick={() => remove(index, parentIndex)} aria-label="delete" className="btn ml-2 btn-outline sm">
          {enforceOptionRules ? <DeleteIcon size={20} /> : <i className="icon-Delete f-20" />}
        </button>
      )}
    </div>
  );
}

export default RenderOptionsItem;
